import { useMemo, useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import JsonLd from '@/components/feature/JsonLd';
import { useSeo, SITE_URL } from '@/hooks/useSeo';
import { categories, getCategory } from '@/mocks/categories';
import { comics } from '@/mocks/comics';
import { chaptersByComic } from '@/mocks/chapters';

type SortKey = 'latest' | 'views' | 'rating';

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'latest', label: '最新更新' },
  { key: 'views', label: '人气最高' },
  { key: 'rating', label: '评分最高' },
];

const PAGE_SIZE = 12;

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const category = getCategory(slug);
  const [sortKey, setSortKey] = useState<SortKey>('latest');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setSortKey('latest');
    setPage(1);
  }, [slug]);

  const tabSlugs = categories.map((c) => c.slug);

  useSeo({
    title: `${category.name}漫画-免费阅读韩国漫画-5177漫画网`,
    description: category.description,
    keywords: `${category.name}漫画,韩国漫画,韩漫,免费阅读,5177漫画网`,
    ogType: 'website',
    canonicalPath: `/category/${slug}`,
  });

  const filtered = useMemo(() => {
    const base = slug === 'all' ? [...comics] : comics.filter((c) => c.category === slug);
    const sorted = [...base];
    if (sortKey === 'views') sorted.sort((a, b) => b.views - a.views);
    else if (sortKey === 'rating') sorted.sort((a, b) => b.rating - a.rating);
    else
      sorted.sort((a, b) => {
        const da = chaptersByComic[a.id]?.[0]?.updatedAt ?? '';
        const db = chaptersByComic[b.id]?.[0]?.updatedAt ?? '';
        return db.localeCompare(da);
      });
    return sorted;
  }, [slug, sortKey]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_URL}/` },
      {
        '@type': 'ListItem',
        position: 2,
        name: `${category.name}漫画`,
        item: `${SITE_URL}/category/${slug}`,
      },
    ],
  };

  const collectionLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${SITE_URL}/category/${slug}#collection`,
    url: `${SITE_URL}/category/${slug}`,
    name: `${category.name}漫画-免费阅读韩国漫画-5177漫画网`,
    description: category.description,
    inLanguage: 'zh-CN',
    isPartOf: { '@id': `${SITE_URL}/#website` },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filtered.map((c, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: c.title,
        url: `${SITE_URL}/comic/${c.id}`,
      })),
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={[breadcrumbLd, collectionLd]} />

      <div className="bg-background-100 border-b border-background-200/70">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-6 md:py-8">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '漫画分类', to: '/category/all' },
              { label: category.name },
            ]}
          />
          <div className="mt-4 flex flex-col md:flex-row md:items-end justify-between gap-3">
            <div>
              <h1 className="font-display text-3xl md:text-4xl text-foreground-950">
                {category.name}漫画
              </h1>
              <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-2xl">{category.description}</p>
            </div>
            <p className="text-sm text-foreground-600 whitespace-nowrap">
              共 <strong className="text-primary-600 font-display">{filtered.length}</strong> 部作品
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 md:px-6 py-6 md:py-8">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div role="tablist" aria-label="漫画分类切换" className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <Link
                key={c.slug}
                role="tab"
                aria-selected={slug === c.slug}
                to={`/category/${c.slug}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                  slug === c.slug
                    ? 'bg-primary-500 text-background-50 font-medium'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200 hover:text-primary-600'
                }`}
              >
                <i className={c.icon} aria-hidden="true" />
                {c.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
            {sortOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => {
                  setSortKey(opt.key);
                  setPage(1);
                }}
                className={`rounded-full px-4 py-1.5 text-sm whitespace-nowrap transition-colors ${
                  sortKey === opt.key
                    ? 'bg-background-50 text-primary-600 font-medium'
                    : 'text-foreground-600 hover:text-primary-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {pageItems.length > 0 ? (
          <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-5">
            {pageItems.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        ) : (
          <div className="mt-16 flex flex-col items-center justify-center text-center py-16">
            <i className="ri-inbox-line text-5xl text-foreground-300" aria-hidden="true" />
            <p className="mt-4 text-sm text-foreground-600">该分类下暂时没有漫画，去其他分类看看吧</p>
            <Link
              to="/category/all"
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 whitespace-nowrap hover:bg-primary-600"
            >
              浏览全部漫画
            </Link>
          </div>
        )}

        {totalPages > 1 && (
          <nav className="mt-10 flex items-center justify-center gap-2" aria-label="分页导航">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-foreground-700 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="上一页"
            >
              <i className="ri-arrow-left-s-line text-lg" aria-hidden="true" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setPage(n)}
                className={`h-9 w-9 rounded-md text-sm transition-colors ${
                  currentPage === n
                    ? 'bg-primary-500 text-background-50 font-medium'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
                aria-label={`第${n}页`}
                aria-current={currentPage === n ? 'page' : undefined}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-foreground-700 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="下一页"
            >
              <i className="ri-arrow-right-s-line text-lg" aria-hidden="true" />
            </button>
          </nav>
        )}
      </div>

      <div className="mx-auto max-w-7xl px-4 md:px-6 pb-6">
        <p className="text-xs leading-6 text-foreground-600">
          {tabSlugs.length > 0 &&
            `5177漫画网${category.name}漫画专区为你精选免费韩国漫画，包含恋爱、校园、奇幻、都市、悬疑等多种题材的中文韩漫作品。所有${category.name}漫画均可在线免费阅读，每日更新最新话，无需注册即可随时沉浸其中。`}
        </p>
      </div>
    </div>
  );
}